import React from 'react'
import { ACTOR_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { DocumentObj } from 'src/adapter/AttachmentAdapter'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { getLinkerTooltipMessage } from 'src/lib/Linkers/Linkers.helpers'
import { DisconnectedLinkerIcon } from 'src/lib/Linkers/Linkers.styles'
import { getReceptorName } from 'src/Utils/Receptors/getReceptorName'
import {
  useHasReceptor,
  useReceptor,
} from 'src/pages/Receptors/receptors.hooks'
import { ViewInSourceButton } from 'src/components/Reusable/Evidence/evidence.components'

export const DocumentSource = ({
  document,
}: {
  document: DocumentObj
}): JSX.Element => {
  const isExternal = document.isLinkedDocument && !!document.actor
  const { data: hasReceptorData, isLoading: hasReceptorLoading } =
    useHasReceptor(document.actor, false, isExternal)
  const hasReceptor = hasReceptorData?.getHasReceptor() || false
  const { data: receptor, isLoading: receptorLoading } = useReceptor({
    modelId: document.actor,
    shouldFetch: hasReceptor,
  })
  const hasInvalidCreds = receptor?.getIscredvalid() === false
  const exceptions = receptor?.getExceptions() || ''
  // Both lookups must settle before either verdict is honest: an unresolved
  // one reads as "no receptor" and then as "valid credentials".
  const healthUnknown =
    (isExternal && hasReceptorLoading) || (hasReceptor && receptorLoading)

  if (isExternal) {
    const name = getReceptorName(document.actor)
    if (healthUnknown) {
      return <p>{name}</p>
    }
    if (!(hasReceptor && !hasInvalidCreds && !exceptions)) {
      // The integration backing this doc is disconnected or erroring, so the
      // source is unreachable — name it and say why instead of linking out.
      return (
        <Tooltip
          id={`source-tooltip-${document.id}`}
          overlayType={TooltipOverlayType.popover}
          placement={TooltipPositions.top}
          tooltipBody={getLinkerTooltipMessage(
            name,
            hasInvalidCreds,
            exceptions,
          )}
          trigger={['hover', 'focus']}
        >
          <FlexRow align={FlexAlign.CENTER} gap={8}>
            <DisconnectedLinkerIcon />
            {name}
          </FlexRow>
        </Tooltip>
      )
    }
    // Not every integration hands back a link to the original — name the
    // source anyway so the column never falls through to a generic label.
    return document.docImportLink ? (
      <ViewInSourceButton
        receptorModelId={document.actor}
        link={document.docImportLink}
      />
    ) : (
      <p>{name}</p>
    )
  }
  switch (document.actortype) {
    case ACTOR_TYPE.TRUSTERO:
      return <p>Trustero</p>
    case ACTOR_TYPE.RECEPTOR:
      return <p>{getReceptorName(document.actor)}</p>
    case ACTOR_TYPE.USER:
    default:
      return <p>Upload</p>
  }
}
